/**
 * Adding a case — one door, two plain choices, one confirm screen.
 *
 * **Phase A (docs/24 Lane I, approved 2 Oct 2026).** What changed, and why:
 *
 * - **The first screen asks what has come in, in two plain choices** (B23,
 *   walkthrough log 32.1): *A new complaint has come in*, or *A case already
 *   under way on paper* — the second then asking how far it has gone, by the
 *   documents' own names and never a D-code.
 * - **A third choice for a file that has gone to court** (Archit, 6 Oct 2026):
 *   the same door as a case on paper, opened at the stage that holds the court
 *   documents, so the officer does not look for it in a list of twenty. The
 *   stage and its documents are read from the published process, and the
 *   upload that follows offers the court's papers first.
 * - **The number is taken at Confirm** (A1, 32.15). An intake is "a new
 *   complaint, not yet numbered"; abandoning it uses no number, and every place
 *   that would print a number says *Not yet numbered*.
 * - **One reading at intake** (A2, 32.6). The complaint is read once, as a
 *   complaint, and the confirm screen is built from that reading on the server
 *   (`GET /cases/{id}/intake`). This screen makes no model call and no
 *   translation call of its own; it only asks, once, for a value the reading
 *   left empty with nothing near it on the page (`ask-again`).
 * - **The confirm screen is the same size however many papers** (A5, 32.9,
 *   32.16): who it is about in four lines, English, with the words as written
 *   one click away; at most five headlines; the papers one line each, folded;
 *   *Needs a look* only when something needs the officer; the gist shown, not
 *   editable; and "Confirm — this becomes 43/2025".
 *
 * Validation is the server's. The screen sends what it showed and the server
 * checks it against the record, so a click can only ever settle what was on
 * the screen.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type {
  IntakeActView,
  IntakeParticularView,
  IntakeSourceView,
  IntakeValueIn,
  IntakeView,
  PaperKindOption,
  ProcessDetail,
  ProcessDocumentView,
  SourceFileView,
} from '../api/client'
import { ErrorEnvelope } from '../components/bits'
import { CourtUploadGuide } from '../components/court-papers'
import {
  askAgainWanted,
  bundleReadProgress,
  intakePollInterval,
  papersPollInterval,
  readStillRunning,
  startBody,
  startIncomplete,
} from '../lib/intake'
import { readDay } from '../lib/blocks'
import {
  COURT_KINDS,
  PLEADINGS,
  belongsToPleading,
  isCourtDocument,
  pleadingOfDescription,
  uploadDescription,
} from '../lib/court'
import type { CourtKind, PleadingKey } from '../lib/court'
import { readDate } from '../lib/period'
import { useSession } from '../lib/session'

// The unit component of the file number is NOT here, and not sent: it is the
// signed-in officer's own factory's code, resolved by the server from their
// identity. What the previews below show comes from the session, read-only.
/** **The same defect as the unit code, one field over.** The office's own
 * script decides the section and subject parts, read off its file-number code:
 * Korwa's `आ.नि.परि.कोरवा` writes Devanagari, a factory recorded as `OFK`
 * writes Latin (Archit, 18 Sep 2026). It is permanent once a number is taken. */
const DEVANAGARI = /[\u0900-\u097F]/
const PARTS = {
  hi: { section: 'डी.एस.', subject: 'अनुशा.' },
  en: { section: 'D.S.', subject: 'DISC.' },
}
const partsFor = (unitCode: string | null | undefined) =>
  unitCode && DEVANAGARI.test(unitCode) ? PARTS.hi : PARTS.en

/** The officer's factory has no file-number code on record yet — the button
 *  stays on screen greyed with this as its reason. */
const CODE_MISSING =
  'This factory’s file-number code is not yet on record, so a case cannot be opened. ' +
  'The code has to come from the factory’s own office — it is never invented.'

/** What every screen prints for a case that has no number yet. The server says
 *  the same (`file_number_label`); this is only the fallback for a row that
 *  carries no label of its own. */
const NOT_YET_NUMBERED = 'Not yet numbered'

export function NewCase() {
  const { user } = useSession()
  // The CGM reads and asks; opening cases is the section's. A null role is
  // unknown, never "officer", and gets the same closed door with honest words.
  if (user?.role == null || user.role === 'cgm') {
    return (
      <div className="page">
        <header>
          <h1>Add a case</h1>
        </header>
        <p className="muted">
          {user?.role === 'cgm'
            ? 'Read and ask only. Cases are added by the Disciplinary Section; '
            : "This account's role is not known to this client, so case actions are not offered. "}
          <Link to="/cases">back to the register</Link>.
        </p>
      </div>
    )
  }
  return <OneDoor />
}

/**
 * The one door. **Which kind of case this is, asked once and never inferred**:
 * the officer answers on the first screen, `historical_entry` carries the
 * answer to the server, and every difference between the two flows reads it.
 */
function OneDoor() {
  const [params, setParams] = useSearchParams()
  const caseId = params.get('case')
  if (caseId) return <IntakeFlow caseId={caseId} />
  return <FirstScreen onStarted={(id) => setParams({ case: id }, { replace: true })} />
}

/**
 * The documents a case on paper may have reached, grouped by stage and in the
 * order a case meets them — the docs/19 numbering the server sends with each
 * document, and the document's own name. **Never a D-code on screen** (B23):
 * the option's value is the key the server expects; its words are the names.
 */
function reachedGroups(
  process: ProcessDetail | undefined,
): { key: string; title: string; documents: ProcessDocumentView[] }[] {
  if (!process) return []
  const stages = process.stages ?? []
  const place = (doc: ProcessDocumentView): [number, number] => {
    const first = (doc.number ?? '').split('/')[0]?.trim() ?? ''
    const [stagePart, withinPart] = first.split('.')
    const stage = Number(stagePart)
    const within = Number(withinPart)
    return Number.isFinite(stage) && stage > 0
      ? [stage, Number.isFinite(within) ? within : 0]
      : [99, doc.n]
  }
  const groups = new Map<number, ProcessDocumentView[]>()
  for (const doc of process.documents ?? []) {
    const [stage] = place(doc)
    groups.set(stage, [...(groups.get(stage) ?? []), doc])
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([stage, documents]) => ({
      key: String(stage),
      title:
        stage === 99
          ? 'Other documents'
          : `Stage ${stage} — ${(stages[stage - 1]?.title ?? '').toLowerCase()}`,
      documents: [...documents].sort((a, b) => place(a)[1] - place(b)[1]),
    }))
}

type Door = '' | 'new' | 'paper' | 'court'

/**
 * The third choice, in the published process's own name for the stage — never
 * a stage name typed here (stop list 6). Until the process has been read there
 * is no name to print, and the sentence stands without one.
 */
function courtDoorTitle(stageTitle: string | null | undefined): string {
  return stageTitle ? `A case at the ${stageTitle} stage` : 'A case that has gone to court'
}

/**
 * The first screen (B23, 32.1): plain choices. A new complaint needs
 * nothing typed — the register numbers it at Confirm and the organisation's
 * clock dates it. A case already under way on paper asks how far it has gone,
 * by document name, and what its folder cover says. A case that has gone to
 * court is a case on paper too — it is opened the same way and checked by the
 * same rules — and asks only which of the court documents is to be prepared.
 */
function FirstScreen({ onStarted }: { onStarted: (caseId: string) => void }) {
  const [door, setDoor] = useState<Door>('')
  const [reached, setReached] = useState('')
  const [serial, setSerial] = useState('')
  const [year, setYear] = useState('')
  const [openedOn, setOpenedOn] = useState('')
  const [note, setNote] = useState('')
  const { user } = useSession()
  const unitCode = user?.unit_code ?? null
  const { section: SECTION, subject: SUBJECT } = partsFor(unitCode)
  const atCourt = door === 'court'
  // A file that has gone to court began before today, as any file on paper
  // did: the same folder cover, the same checks, one door on the server.
  const onPaper = door === 'paper' || atCourt

  /* The rail as the pinned version writes it — never a literal list, and never
     a title typed into this file (stop list 6). */
  const process = useQuery({ queryKey: ['process'], queryFn: () => api.getProcess() })

  /* The register year of a file opened on 14/06/2024 is 2024, and the officer
     has just typed that date: prefilled, on screen, and editable. */
  const registerYear = year.trim() || openedOn.slice(0, 4)

  /* The stage that holds the court documents, and its title, as the published
     process writes them. A process with no such stage offers the choice
     greyed, with the reason, rather than leaving it out. */
  const groups = reachedGroups(process.data)
  const courtGroup =
    groups.find((group) => group.documents.some((doc) => isCourtDocument(doc.id))) ?? null
  const courtStageTitle =
    (process.data?.stages ?? []).find((stage) => String(stage.n) === courtGroup?.key)?.title ??
    null
  const courtDocuments = (courtGroup?.documents ?? []).filter((doc) => isCourtDocument(doc.id))
  const noCourtStage = process.isSuccess && courtGroup === null

  // What was chosen under one door is not carried to another: a document of
  // the inquiry is not an answer to "which court document".
  const choose = (next: Door) => {
    if (next !== door) setReached('')
    setDoor(next)
  }

  // What Start sends for each door is one pure function (`lib/intake`), so
  // "a file at court sends no folder cover" is pinned and not only drawn.
  const choice = {
    section: SECTION,
    subject: SUBJECT,
    reached,
    serial,
    year: registerYear,
    openedOn,
    note,
  }
  const start = useMutation({
    mutationFn: () => {
      if (door === '') throw new Error('Choose what has come in.')
      return api.startIntake(startBody({ door, ...choice }))
    },
    onSuccess: (receipt) => onStarted(receipt.case_id),
  })

  const incomplete = startIncomplete({ door, ...choice })

  return (
    <div className="page">
      <header>
        <h1>Add a case</h1>
      </header>

      <OpenIntakes onResume={onStarted} />

      <div className="card">
        <header>
          <h2>What has come in?</h2>
        </header>
        <div className="body">
          <div className="intake-doors" role="radiogroup" aria-label="What has come in?">
            <button
              type="button"
              role="radio"
              aria-checked={door === 'new'}
              className={`intake-door${door === 'new' ? ' on' : ''}`}
              data-testid="door-new"
              onClick={() => choose('new')}
            >
              <strong>A new complaint has come in</strong>
              <span className="small muted">
                Upload it next. The case is numbered when you confirm who it is about.
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={door === 'paper'}
              className={`intake-door${door === 'paper' ? ' on' : ''}`}
              data-testid="door-paper"
              onClick={() => choose('paper')}
            >
              <strong>A case already under way on paper</strong>
              <span className="small muted">
                A file that has already begun — add its papers next.
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={atCourt}
              className={`intake-door${atCourt ? ' on' : ''}`}
              data-testid="door-court"
              disabled={noCourtStage}
              onClick={() => choose('court')}
            >
              <strong>{courtDoorTitle(courtStageTitle)}</strong>
              <span className="small muted">
                {noCourtStage
                  ? 'The published process has no court documents, so no case can be opened at that stage.'
                  : 'A file that has gone to court — add the papers of the court case next.'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {onPaper ? (
        <div className="card" data-testid="cover">
          <header>
            <h2>{atCourt ? 'What is to be prepared?' : 'How far has it gone?'}</h2>
          </header>
          <div className="body">
            <label className="field">
              <span className="label">
                {atCourt ? 'The document to be prepared' : 'The last document the file holds'}
              </span>
              <select
                aria-label={atCourt ? 'What is to be prepared?' : 'How far has it gone?'}
                value={reached}
                onChange={(event) => setReached(event.target.value)}
                data-testid="reached"
              >
                <option value="">— choose —</option>
                {atCourt
                  ? courtDocuments.map((doc) => (
                      <option value={doc.id} key={doc.id}>
                        {doc.number ? `${doc.number} · ` : ''}
                        {doc.short_title ?? doc.title}
                      </option>
                    ))
                  : groups.map((group) => (
                      <optgroup key={group.key} label={group.title}>
                        {group.documents.map((doc) => (
                          <option value={doc.id} key={doc.id}>
                            {doc.number ? `${doc.number} · ` : ''}
                            {doc.short_title ?? doc.title}
                          </option>
                        ))}
                      </optgroup>
                    ))}
              </select>
              {process.isLoading ? (
                <div className="small muted">Reading the register&apos;s process…</div>
              ) : atCourt ? (
                <div className="small muted">
                  A cross-check. The papers you add next decide what can be drafted.
                </div>
              ) : (
                <div className="small muted">
                  A cross-check. The papers you add next decide where the case actually is.
                </div>
              )}
            </label>

            {atCourt ? (
              <p className="small muted" data-testid="court-no-cover">
                Nothing is asked off a folder cover. The file is entered as of today and
                numbered when you confirm it, with the register&apos;s next.
              </p>
            ) : (
              <FolderCover
                unitCode={unitCode}
                section={SECTION}
                subject={SUBJECT}
                serial={serial}
                onSerial={setSerial}
                registerYear={registerYear}
                onYear={setYear}
                openedOn={openedOn}
                onOpenedOn={setOpenedOn}
                note={note}
                onNote={setNote}
              />
            )}
          </div>
        </div>
      ) : null}

      {start.error ? <ErrorEnvelope error={start.error} /> : null}
      {unitCode ? null : <p className="small muted">{CODE_MISSING}</p>}
      <div className="confirm-actions">
        <button
          type="button"
          className="primary"
          disabled={start.isPending || incomplete || !unitCode}
          data-testid="start"
          onClick={() => start.mutate()}
        >
          {start.isPending ? 'Starting…' : onPaper ? 'Open the file' : 'Start'}
        </button>
        {door === '' ? (
          <span className="small muted">Choose what has come in.</span>
        ) : onPaper && incomplete ? (
          <span className="small muted">
            {atCourt
              ? 'Say what is to be prepared.'
              : 'Say how far it has gone and when the file was opened.'}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * What the folder cover says — asked of a case already under way on paper,
 * and **never of a file at court** (Archit, 6 Oct 2026), which the server
 * enters as of today.
 */
function FolderCover({
  unitCode,
  section,
  subject,
  serial,
  onSerial,
  registerYear,
  onYear,
  openedOn,
  onOpenedOn,
  note,
  onNote,
}: {
  unitCode: string | null
  section: string
  subject: string
  serial: string
  onSerial: (value: string) => void
  registerYear: string
  onYear: (value: string) => void
  openedOn: string
  onOpenedOn: (value: string) => void
  note: string
  onNote: (value: string) => void
}) {
  return (
    <>
      <h3 className="intake-sub">What the folder cover says</h3>
      <p className="mono small muted deva">
        {unitCode ?? '—'} / {section} / {subject} / {serial.trim() || '…'} /{' '}
        {registerYear || '…'}
      </p>
      <div className="row form">
        <div className="field">
          <label htmlFor="serial">
            Serial <span className="small muted">Optional.</span>
          </label>
          <input
            id="serial"
            inputMode="numeric"
            placeholder="blank: the next free one, at Confirm"
            value={serial}
            onChange={(e) => onSerial(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="year">Year</label>
          <input
            id="year"
            inputMode="numeric"
            placeholder={openedOn.slice(0, 4) || "the register's year"}
            value={registerYear}
            onChange={(e) => onYear(e.target.value)}
          />
          <div className="small muted">
            Serials run per year — 67 of 2024 is not 67 of 2020.
          </div>
        </div>
        <div className="field">
          <label htmlFor="opened">The file was opened on</label>
          <input
            id="opened"
            type="date"
            value={openedOn}
            onChange={(e) => onOpenedOn(e.target.value)}
            required
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="note">
          Where the past record came from <span className="small muted">Optional.</span>
        </label>
        <input
          id="note"
          placeholder="e.g. Original file received from M.T. Section on 12.08.2026"
          value={note}
          onChange={(e) => onNote(e.target.value)}
        />
      </div>
      <p className="small muted">
        The file is numbered when you confirm it, with the serial above or — left blank — the
        register&apos;s next.
      </p>
    </>
  )
}

/**
 * Intakes started and never confirmed (entry 4, promoted 29 Aug 2026). Offered,
 * never refused: two concurrent intakes are lawful. **Not yet numbered**, so
 * withdrawing one costs the register nothing.
 */
function OpenIntakes({ onResume }: { onResume: (caseId: string) => void }) {
  const openIntakes = useQuery({
    queryKey: ['open-intakes'],
    queryFn: () => api.listOpenIntakes(),
  })
  const rows = openIntakes.data ?? []
  if (rows.length === 0) return null
  return (
    <div className="card" data-testid="open-intakes">
      <header>
        <h2>An intake is already open</h2>
        <span className="small muted">resume it, or start another — both are lawful</span>
      </header>
      <div className="body">
        {rows.map((row) => (
          <div
            key={row.case_id}
            className="row"
            style={{ alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}
          >
            <span className={row.file_number ? 'mono deva' : 'muted'}>
              {row.file_number ?? NOT_YET_NUMBERED}
            </span>
            <span className="small muted">
              started {readDate(row.opened_on)}
              {row.started_by ? ` by ${row.started_by}` : ''}
              {row.papers
                ? ` · ${row.papers} paper${row.papers === 1 ? '' : 's'}`
                : ' · no paper yet'}
            </span>
            <button
              type="button"
              className="btn"
              data-testid={`resume-${row.case_id}`}
              onClick={() => onResume(row.case_id)}
            >
              Continue this intake
            </button>
            <AbandonButton
              caseId={row.case_id}
              label="Withdraw it"
              numbered={Boolean(row.file_number)}
              onAbandoned={() => void openIntakes.refetch()}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * After Start: the complaint (or the file's papers), its one reading, and the
 * confirm screen built from it.
 */
function IntakeFlow({ caseId }: { caseId: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // The officer says when the papers are in: only the officer holding the
  // folder knows whether a file with three papers is finished.
  const [papersDone, setPapersDone] = useState(false)

  const detail = useQuery({ queryKey: ['case', caseId], queryFn: () => api.getCase(caseId) })
  const onPaper = Boolean(detail.data?.historical_entry)

  const papers = useQuery({
    queryKey: ['source-files', caseId],
    queryFn: () => api.listSourceFiles(caseId),
    // An officer who switches tabs during a two-minute OCR must come back to a
    // moved screen, not a frozen count.
    refetchIntervalInBackground: true,
    refetchInterval: (query) => papersPollInterval(query.state.data?.files ?? []),
  })
  const live = (papers.data?.files ?? []).filter((row) => !row.removed_at)
  const hasPaper = live.length > 0

  /* The confirm screen's data — built on the server from the one reading. It
     is polled only while something is still on its way; nothing behind it
     calls a model or translates anything, so a refetch on focus costs nothing
     (the lesson of 32.5). */
  const intake = useQuery({
    queryKey: ['intake', caseId],
    queryFn: () => api.getIntake(caseId),
    enabled: hasPaper,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => intakePollInterval(query.state.data ?? null),
  })

  if (papers.isLoading || detail.isLoading) {
    return <div className="empty">Reading the case…</div>
  }

  const view = intake.data ?? null
  const reading = !view || view.reading === 'reading'
  const progress = bundleReadProgress(live)
  const scanning = live.find(readStillRunning) ?? null
  const showConfirm =
    hasPaper && view != null && view.reading !== 'reading' && (!onPaper || papersDone)

  return (
    <div className="page">
      <header>
        <div className="small muted case-headline">
          <span>
            <Link to="/cases">Cases</Link> ›{' '}
            <span className={view?.numbered ? 'mono deva' : undefined}>
              {view?.file_number_label ?? detail.data?.file_number ?? NOT_YET_NUMBERED}
            </span>
          </span>
          <AbandonButton caseId={caseId} numbered={Boolean(view?.numbered)} />
        </div>
        <h1>
          {isCourtDocument(detail.data?.declared_reached)
            ? courtDoorTitle(detail.data?.stage_title)
            : onPaper
              ? 'A case already under way on paper'
              : 'A new complaint'}
        </h1>
      </header>

      {onPaper && !papersDone ? (
        <PapersStep caseId={caseId} onDone={() => setPapersDone(true)} />
      ) : !onPaper && !hasPaper ? (
        <UploadComplaint caseId={caseId} onUploaded={() => void papers.refetch()} />
      ) : null}

      {hasPaper && (!onPaper || papersDone) && reading ? (
        <div className="card" data-testid="read-progress">
          <header>
            <h2>
              {onPaper
                ? `Reading the papers — ${progress.landed} of ${progress.total} read`
                : 'Reading the complaint'}
            </h2>
            {scanning ? <span className="small muted mono">{scanning.filename}</span> : null}
          </header>
          <div className="body">
            <p>
              {scanning?.read_pages_total
                ? `${scanning.read_pages_done} of ${scanning.read_pages_total} pages through the OCR.`
                : 'Reading what it says — once, for the whole case record.'}
            </p>
            <p className="small muted">
              A Word file takes a few seconds; a scan a minute or two. This screen updates
              itself.
            </p>
          </div>
        </div>
      ) : null}

      {intake.error ? <ErrorEnvelope error={intake.error} /> : null}

      {view?.held ? (
        <HeldPaper
          caseId={caseId}
          sentence={view.held}
          words={view.reading_words}
          paperId={view.papers?.[0]?.id ?? null}
          onRemoved={() => void papers.refetch()}
        />
      ) : null}

      {showConfirm && view ? (
        <ConfirmScreen
          caseId={caseId}
          view={view}
          papers={live}
          onConfirmed={() => {
            void queryClient.invalidateQueries({ queryKey: ['cases'] })
            void queryClient.invalidateQueries({ queryKey: ['case-identifiers'] })
            void queryClient.invalidateQueries({ queryKey: ['open-intakes'] })
            // This screen read the case too, and under the ten-second stale
            // time the case page would otherwise open on that copy.
            void queryClient.invalidateQueries({ queryKey: ['case', caseId] })
            navigate(`/cases/${caseId}`)
          }}
        />
      ) : null}
    </div>
  )
}

function UploadComplaint({ caseId, onUploaded }: { caseId: string; onUploaded: () => void }) {
  const picker = useRef<HTMLInputElement>(null)
  // **A Word file or a PDF** (Archit, 29 Sep 2026 — walkthrough log 32.2). The
  // `accept` filter is advisory in every browser, so a picked file is checked
  // here too.
  const [refused, setRefused] = useState<string | null>(null)
  const upload = useMutation({
    mutationFn: (file: File) =>
      // **The door settles the kind** (Phase A, option 1): this box is the
      // complaint's, so the paper is read once, as a complaint, never on a
      // guess. `शिकायत` stays as the label the documents' reference list
      // prints (first real comparison, 1 Sep 2026).
      api.uploadSourceFile(caseId, file, {
        other_description: 'शिकायत',
        paper_kind: 'complaint',
      }),
    onSuccess: onUploaded,
  })
  return (
    <div className="card">
      <header>
        <h2>The complaint</h2>
      </header>
      <div className="body">
        <input
          ref={picker}
          type="file"
          accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
          aria-label="The complaint — a Word file or a PDF"
          data-testid="complaint-file"
          style={{ display: 'none' }}
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (!file) return
            const name = file.name.toLowerCase()
            if (!name.endsWith('.docx') && !name.endsWith('.pdf')) {
              setRefused(`${file.name} is not a Word file or a PDF.`)
              return
            }
            setRefused(null)
            upload.mutate(file)
          }}
        />
        <button
          className="btn"
          data-testid="upload-complaint"
          disabled={upload.isPending}
          onClick={() => picker.current?.click()}
        >
          {upload.isPending ? 'Uploading…' : 'Upload the complaint (Word or PDF)'}
        </button>{' '}
        <a href="/ocr-tool" target="_blank" rel="noreferrer" className="small">
          Open the OCR tool
        </a>
        {refused ? (
          <div className="blocked" style={{ marginTop: 8 }} data-testid="complaint-refused">
            <div>
              <div className="why">{refused}</div>
            </div>
          </div>
        ) : null}
        {upload.error ? <ErrorEnvelope error={upload.error} /> : null}
      </div>
    </div>
  )
}

/**
 * The paper given at *The complaint* was not read as one: its text speaks of a
 * finding or a penalty, so the reader held it rather than read it on the wrong
 * question (Lane R). Nothing is waited for; the officer checks the file, and
 * takes it off with a reason to upload the right one.
 */
function HeldPaper({
  caseId,
  sentence,
  words,
  paperId,
  onRemoved,
}: {
  caseId: string
  sentence: string
  words: string
  paperId: string | null
  onRemoved: () => void
}) {
  const [reason, setReason] = useState('Not the complaint — the wrong file was uploaded.')
  const remove = useMutation({
    mutationFn: () =>
      api.removeSourceFile(caseId, paperId ?? '', { preflight: false, reason: reason.trim() }),
    onSuccess: onRemoved,
  })
  return (
    <div className="blocked" data-testid="held-paper">
      <div>
        <div className="why">
          <strong>{words}</strong> {sentence}
        </div>
        {paperId ? (
          <div className="row form" style={{ marginTop: 8 }}>
            <input
              aria-label="Why it comes off the file"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              style={{ minWidth: 320 }}
            />
            <button
              type="button"
              className="btn"
              disabled={!reason.trim() || remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? 'Removing…' : 'Take it off, and upload the right file'}
            </button>
          </div>
        ) : null}
        {remove.error ? <ErrorEnvelope error={remove.error} /> : null}
      </div>
    </div>
  )
}

/**
 * The way out of a started intake, wherever the officer meets one.
 *
 * **Withdrawn with a reason, never deleted.** Phase A: an intake not yet
 * numbered uses no number, so withdrawing it leaves the register untouched; one
 * started before Phase A already holds a serial, which stays retired.
 */
function AbandonButton({
  caseId,
  label = 'Abandon this intake',
  numbered = false,
  onAbandoned,
}: {
  caseId: string
  label?: string
  numbered?: boolean
  onAbandoned?: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const abandon = useMutation({
    mutationFn: () => api.abandonIntake(caseId, { preflight: false, reason: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cases'] })
      void queryClient.invalidateQueries({ queryKey: ['open-intakes'] })
      if (onAbandoned) onAbandoned()
      else navigate('/cases')
    },
  })
  if (!open) {
    return (
      <button
        type="button"
        className="btn"
        onClick={() => setOpen(true)}
        data-testid={`abandon-${caseId}`}
      >
        {label}
      </button>
    )
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        aria-label="Why the intake is being abandoned"
        placeholder="why — this goes on the record"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        style={{ width: 260 }}
      />
      <button
        type="button"
        disabled={reason.trim().length < 5 || abandon.isPending}
        onClick={() => abandon.mutate()}
      >
        {abandon.isPending ? 'Withdrawing…' : 'Withdraw it'}
      </button>
      <button type="button" className="quiet" onClick={() => setOpen(false)}>
        Keep going
      </button>
      <span className="small muted">
        {numbered
          ? 'Its number stays retired, with your reason.'
          : 'It has no number yet, so none is used.'}
      </span>
      {abandon.error ? <ErrorEnvelope error={abandon.error} /> : null}
    </span>
  )
}

/* --------------------------------------------------------- the confirm screen */

type Settled = {
  /** What the officer typed for a line the papers did not give (or chose to
   *  type over the page's wording). */
  typed: string
  english: string
  /** The page's wording, accepted. */
  accepted: boolean
  /** On a court case: the officer chose to type over what the papers gave. */
  declined: boolean
}

const EMPTY: Settled = { typed: '', english: '', accepted: false, declined: false }

/** What the papers of a court case print of one of his lines, and where. */
type CourtOffer = {
  field: 'name' | 'ticket' | 'post' | 'section'
  as_written: string
  filename: string
  page: number
}

function sourceWords(source: IntakeSourceView | null | undefined): string | null {
  if (!source?.ordinal) return null
  return `paper ${source.ordinal}${source.page ? `, page ${source.page}` : ''}`
}

/** What the click sends for one line — exactly what the screen showed. */
function lineValue(
  line: IntakeParticularView,
  settled: Settled,
  offer: CourtOffer | null = null,
): IntakeValueIn | null {
  if (line.status === 'found' && line.value) {
    return {
      as_written: line.value.as_written,
      english: line.value.english ?? null,
      basis: 'read',
    }
  }
  if (line.status === 'suggested' && line.suggestion && settled.accepted) {
    return {
      as_written: line.suggestion.as_written,
      english: settled.english.trim() || null,
      basis: 'suggested',
    }
  }
  if (settled.typed.trim()) {
    return {
      as_written: settled.typed.trim(),
      english: settled.english.trim() || null,
      basis: 'typed',
    }
  }
  // A court case: the line stands filled with what the papers print, and that
  // is what Confirm sends unless the officer typed over it. The server records
  // it as read from its page only where it equals the offer it kept — so the
  // click still settles nothing but what this screen showed.
  if (offer && !settled.declined) {
    return {
      as_written: offer.as_written,
      english: settled.english.trim() || null,
      basis: 'typed',
    }
  }
  return null
}

/**
 * The confirm screen (docs/24 item 4) — the same size however many papers.
 *
 * Nothing here is computed from the papers on the client: who it is about, the
 * headlines, the gist and *Needs a look* are the server's, read off the one
 * reading. The officer's part is to accept the page's wording where the app
 * offers it, to type what the papers do not say, and to confirm.
 */
function ConfirmScreen({
  caseId,
  view,
  papers,
  onConfirmed,
}: {
  caseId: string
  view: IntakeView
  papers: SourceFileView[]
  onConfirmed: () => void
}) {
  const [settled, setSettled] = useState<Record<string, Settled>>({})
  const [adopt, setAdopt] = useState(false)
  const [acts, setActs] = useState<Record<string, ActChoice>>({})
  const queryClient = useQueryClient()
  const asked = useRef<Set<string>>(new Set())

  const askAgain = useMutation({
    mutationFn: (field: IntakeParticularView['field']) =>
      api.askIntakeAgain(caseId, { preflight: false, field }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['intake', caseId] }),
  })

  /* **One small call, once, for a line the reading left empty with nothing
     near it on the page** (docs/24 item 3). Fired by the screen the moment it
     sees such a line, never twice from one screen — and the server keeps the
     same promise across screens and reloads, so a refetch can never re-spend. */
  useEffect(() => {
    if (askAgain.isPending) return
    const next = view.who.find((line) => askAgainWanted(line, asked.current))
    if (next) {
      asked.current.add(next.field)
      askAgain.mutate(next.field)
    }
  }, [view.who, askAgain])

  const holder = view.ticket_holder ?? null
  // What the papers of a court case print of him — asked for once, when the
  // papers have been read. The server's offer; the name from the pleading's
  // title stands in until it arrives.
  const courtOffers = useQuery({
    queryKey: ['court-particulars', caseId],
    queryFn: () => api.readCourtParticulars(caseId),
    enabled: Boolean(view.at_court) && view.reading === 'done' && !view.confirmed,
    staleTime: Infinity,
    retry: false,
  })
  const titleName =
    view.court_name && typeof view.court_name['as_written'] === 'string'
      ? [
          {
            field: 'name' as const,
            as_written: view.court_name['as_written'] as string,
            filename: String(view.court_name['filename'] ?? ''),
            ordinal: 0,
            page: Number(view.court_name['page'] ?? 1),
          },
        ]
      : []
  const offered = courtOffers.data?.offers?.length ? courtOffers.data.offers : titleName
  const courtGist = view.at_court ? (courtOffers.data?.gist ?? null) : null
  const offerFor = (field: string): CourtOffer | null =>
    view.at_court ? (offered.find((offer) => offer.field === field) ?? null) : null
  const adopting = Boolean(adopt && holder)
  const name = view.who.find((line) => line.field === 'name')
  const nameValue = name ? lineValue(name, settled.name ?? EMPTY, offerFor('name')) : null
  const nameMissing = !adopting && !nameValue

  const [refusedActs, setRefusedActs] = useState<string[]>([])
  const chosenActs = (view.acts ?? [])
    .map((act) => ({ act, choice: acts[act.key] ?? defaultChoice(act) }))
    .filter(({ choice }) => choice.on && choice.chosen)

  const confirm = useMutation({
    mutationFn: () =>
      api.confirmCase(caseId, {
        preflight: false,
        name: adopting ? null : nameValue,
        ticket: adopting ? null : valueFor(view, 'ticket', settled, offerFor('ticket')),
        post: adopting ? null : valueFor(view, 'post', settled, offerFor('post')),
        section: adopting ? null : valueFor(view, 'section', settled, offerFor('section')),
        // Exactly the lines shown, read-only — so what is settled is what the
        // officer saw.
        subject_en: view.subject?.english ?? null,
        // On a court case the gist shown is the one read off the court papers.
        gist: view.gist ?? courtGist ?? null,
        use_employee_id: adopting && holder ? holder.employee_id : null,
        acts: chosenActs.map(({ act, choice }) => ({
          key: act.key as 'route' | 'issued' | 'served' | 'reply' | 'answers',
          on: choice.on,
          answers:
            act.key === 'answers'
              ? Object.entries(choice.answers)
                  .filter(([, answer]) => answer)
                  .map(([charge, answer]) => ({
                    charge: Number(charge),
                    answer: answer as 'admitted' | 'not_admitted',
                  }))
              : [],
        })),
      }),
    onSuccess: (receipt) => {
      const refused = (receipt.acts ?? []).filter((act) => !act.recorded)
      if (refused.length === 0) onConfirmed()
      else setRefusedActs(refused.map((act) => `${act.label}: ${act.message}`))
    },
  })

  const blocked = !view.can_confirm
    ? (view.blocked_reason ?? 'This intake cannot be confirmed now.')
    : nameMissing
      ? 'His name is needed — it is who this proceeding is about. Type it above.'
      : null

  return (
    <form
      data-testid="confirm-screen"
      onSubmit={(event) => {
        event.preventDefault()
        if (!blocked) confirm.mutate()
      }}
    >
      {view.needs_a_look && view.needs_a_look.length > 0 ? (
        <div className="card" data-testid="needs-a-look">
          <header>
            <h2>Needs a look</h2>
          </header>
          <div className="body">
            <ul className="intake-look">
              {view.needs_a_look.map((row, index) => (
                <li key={`${row.kind}-${row.field ?? ''}-${index}`} className="small">
                  {row.text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="card">
        <header>
          <h2>Who it is about</h2>
          <span className="small muted">
            as the papers give it — the words as written one click away
          </span>
        </header>
        <div className="body">
          {view.at_court && courtOffers.data?.says ? (
            /* A court case: the reader of stages 1 to 3 does not read these
               papers. One narrow reading gives what they print of him, each
               value checked against its page by the server, and the lines
               below stand filled with it. What the reading could not use is
               said here. */
            <p className="small muted" data-testid="court-particulars">
              {courtOffers.data.says}
            </p>
          ) : null}
          {holder ? (
            <div className="blocked unsettled" data-testid="ticket-existing">
              <div>
                <div className="why">
                  <strong>
                    Ticket {view.who.find((line) => line.field === 'ticket')?.value?.as_written}{' '}
                    is already on record
                  </strong>
                  {' — '}
                  {holder.name_en ?? <span className="deva">{holder.name_hi}</span>}
                  {holder.designation_en || holder.designation_hi ? (
                    <>
                      {' '}
                      ·{' '}
                      {holder.designation_en ?? (
                        <span className="deva">{holder.designation_hi}</span>
                      )}
                    </>
                  ) : null}
                  {', '}
                  {holder.open_cases === 1
                    ? 'one open proceeding.'
                    : `${holder.open_cases} open proceedings.`}{' '}
                  Check the paper — a wrong digit puts this case on the wrong person.
                </div>
                <label className="inline-choice">
                  <input
                    type="radio"
                    name="employee-choice"
                    checked={adopt}
                    onChange={() => setAdopt(true)}
                    data-testid="use-existing"
                  />
                  <span>Same person — use that record</span>
                </label>
                <label className="inline-choice">
                  <input
                    type="radio"
                    name="employee-choice"
                    checked={!adopt}
                    onChange={() => setAdopt(false)}
                  />
                  <span>Different person — a new record</span>
                </label>
              </div>
            </div>
          ) : null}

          {adopting && holder ? (
            <p className="small" data-testid="adopting">
              This case attaches to <strong>{holder.name_en ?? holder.name_hi}</strong>. Their
              record is not edited from an intake; correct it on the case page if needed.
            </p>
          ) : (
            <dl className="read-only-facts intake-who" data-testid="confirm-particulars">
              {view.who.map((line) => (
                <WhoLine
                  key={line.field}
                  line={line}
                  settled={settled[line.field] ?? EMPTY}
                  asking={askAgain.isPending && askAgain.variables === line.field}
                  offer={offerFor(line.field)}
                  reading={Boolean(view.at_court) && courtOffers.isLoading}
                  onChange={(next) => setSettled((was) => ({ ...was, [line.field]: next }))}
                />
              ))}
            </dl>
          )}
          {askAgain.error ? <ErrorEnvelope error={askAgain.error} /> : null}
        </div>
      </div>

      <div className="card">
        <header>
          <h2>What it is about</h2>
          {view.headlines_more ? (
            <span className="small muted">{view.headlines_more} more on the case page</span>
          ) : null}
        </header>
        <div className="body">
          {view.at_court ? (
            /* A court case: what the matter is, in the app's words from the
               pleading's first pages. Every date and number in it was checked
               to be on those pages. Shown, and settled as shown at Confirm. */
            courtOffers.isLoading ? (
              <p className="small muted">Reading what the matter is…</p>
            ) : courtGist ? (
              <p data-testid="court-gist">
                {courtGist}{' '}
                <span className="small muted">
                  — as the app read it from the pleading. Shown for reference; no document
                  prints it.
                </span>
              </p>
            ) : (
              <p className="small muted">
                The papers read so far do not say enough for a gist. The replies do not need
                one.
              </p>
            )
          ) : (view.headlines ?? []).length === 0 ? (
            <p className="small muted">
              The reading found no numbered allegation. The case page shows everything it read.
            </p>
          ) : (
            <ol className="intake-headlines" data-testid="confirm-headlines">
              {(view.headlines ?? []).map((headline) => (
                <li key={headline.source.item_id ?? headline.text}>
                  <details>
                    <summary>
                      {headline.text}
                      {headline.on ? (
                        <span className="small muted"> · {readDate(headline.on)}</span>
                      ) : null}
                    </summary>
                    {headline.quote ? (
                      <p className="small deva intake-quote">{headline.quote}</p>
                    ) : null}
                    <p className="small muted">{sourceWords(headline.source)}</p>
                  </details>
                </li>
              ))}
            </ol>
          )}
          {view.subject ? (
            <div className="read-only-block" data-testid="confirm-subject">
              <div className="label">Its subject line</div>
              <details>
                <summary className="prose">
                  {view.subject.english ?? (
                    <span className="deva">{view.subject.as_written}</span>
                  )}
                </summary>
                <p className="small deva">{view.subject.as_written}</p>
                <p className="small muted">{sourceWords(view.subject.source)}</p>
              </details>
            </div>
          ) : null}
          {view.gist ? (
            <div
              className="read-only-block"
              data-testid="confirm-gist"
              style={{ marginTop: 10 }}
            >
              <div className="label">The gist</div>
              <p className="prose">{view.gist}</p>
            </div>
          ) : null}
        </div>
      </div>

      {(view.acts ?? []).length > 0 ? (
        <ActsCard
          acts={view.acts ?? []}
          choices={acts}
          onChange={(key, choice) => setActs((was) => ({ ...was, [key]: choice }))}
        />
      ) : null}

      <details className="card intake-papers" data-testid="confirm-papers">
        <summary>
          <strong>The papers</strong>{' '}
          <span className="small muted">
            {(view.papers ?? []).length} on the file — one line each
          </span>
        </summary>
        <ul className="body">
          {(view.papers ?? []).map((paper) => (
            <li key={paper.id} className="small">
              <span className="mono">{paper.ordinal}</span> · {paper.kind_label} ·{' '}
              <a
                className="mono"
                href={api.sourceFilePath(caseId, paper.id)}
                target="_blank"
                rel="noreferrer"
              >
                {paper.filename}
              </a>{' '}
              · <span className="muted">{paper.reading_words}</span>
              {paper.summary ? <div className="muted">{paper.summary}</div> : null}
            </li>
          ))}
        </ul>
      </details>

      {refusedActs.length > 0 ? (
        <div className="blocked" data-testid="acts-refused">
          <div>
            <div className="why">
              The case is confirmed. These were not recorded — the case page offers them again:
            </div>
            <ul>
              {refusedActs.map((text) => (
                <li key={text} className="small">
                  {text}
                </li>
              ))}
            </ul>
            <Link to={`/cases/${caseId}`}>Open the case</Link>
          </div>
        </div>
      ) : null}
      {confirm.error ? <ErrorEnvelope error={confirm.error} /> : null}
      <div className="confirm-actions">
        <button
          type="submit"
          className="primary"
          disabled={confirm.isPending || Boolean(blocked) || refusedActs.length > 0}
          data-testid="confirm-open"
        >
          {confirm.isPending ? 'Confirming…' : view.confirm_label}
        </button>
        {blocked ? <span className="small muted">{blocked}</span> : null}
      </div>
      {papers.length === 0 ? null : <p className="small muted">{view.identifier_line}</p>}
    </form>
  )
}

function valueFor(
  view: IntakeView,
  field: IntakeParticularView['field'],
  settled: Record<string, Settled>,
  offer: CourtOffer | null = null,
): IntakeValueIn | null {
  const line = view.who.find((candidate) => candidate.field === field)
  return line ? lineValue(line, settled[field] ?? EMPTY, offer) : null
}

/**
 * One of his four lines. English first, the words as written one click away,
 * with the paper and page; the page's own wording to accept where the reading
 * did not copy it exactly; a small box where the papers say nothing. Only the
 * name is required.
 */
function WhoLine({
  line,
  settled,
  asking,
  offer = null,
  reading = false,
  onChange,
}: {
  line: IntakeParticularView
  settled: Settled
  asking: boolean
  /** A court case: what its papers print for this line, and where. */
  offer?: CourtOffer | null
  /** A court case: the papers are being read for his particulars now. */
  reading?: boolean
  onChange: (next: Settled) => void
}) {
  const label = line.label
  const hindi = offer ? DEVANAGARI.test(offer.as_written) : false
  return (
    <div data-testid={`who-${line.field}`}>
      <dt>{label}</dt>
      <dd>
        {line.status === 'found' && line.value ? (
          <details className="intake-value">
            <summary>
              <span className={line.value.english ? undefined : 'deva'}>
                {line.value.english ?? line.value.as_written}
              </span>
              {line.value.unclear ? <span className="chip warn">not certain</span> : null}
              {line.value.asked ? (
                <span className="small muted"> · found on a second look</span>
              ) : null}
            </summary>
            <span className="small">
              As written: <span className="deva">{line.value.as_written}</span>
              {sourceWords(line.value.source) ? ` — ${sourceWords(line.value.source)}` : ''}
            </span>
          </details>
        ) : line.status === 'waiting' ? (
          <span className="small muted">Reading…</span>
        ) : reading ? (
          <span className="small muted">Reading it from the papers…</span>
        ) : offer && !settled.declined ? (
          /* Filled from the court papers (Archit, 6 Oct 2026: "if the app has
             extracted it should just fill"). Shown with its page; one click to
             type over it. Written at Confirm, as every line on this screen is. */
          <div className="intake-offered" data-testid={`offered-${line.field}`}>
            <span>
              <strong className={hindi ? 'deva' : undefined}>{offer.as_written}</strong>{' '}
              <span className="small muted">
                — {offer.filename}, page {offer.page}
              </span>{' '}
              <button
                type="button"
                className="link small"
                data-testid={`change-${line.field}`}
                onClick={() =>
                  onChange({ ...settled, declined: true, typed: offer.as_written })
                }
              >
                Change
              </button>
            </span>
            {hindi && line.field !== 'ticket' ? (
              <input
                aria-label={`${label} in English`}
                placeholder="in English (optional)"
                value={settled.english}
                onChange={(event) => onChange({ ...settled, english: event.target.value })}
              />
            ) : null}
          </div>
        ) : line.status === 'suggested' && line.suggestion ? (
          <div className="intake-suggest">
            <span className="small">
              The page has <span className="deva">{line.suggestion.as_written}</span>
              {sourceWords(line.suggestion.source)
                ? ` (${sourceWords(line.suggestion.source)})`
                : ''}
              {line.suggestion.read_as ? (
                <span className="muted">
                  {' '}
                  — the reading wrote <span className="deva">{line.suggestion.read_as}</span>,
                  which is not on the page.
                </span>
              ) : null}
            </span>
            <label className="inline-choice">
              <input
                type="checkbox"
                checked={settled.accepted}
                onChange={(event) =>
                  onChange({ ...settled, accepted: event.target.checked, typed: '' })
                }
                data-testid={`accept-${line.field}`}
              />
              <span>Use the page&apos;s wording</span>
            </label>
            {settled.accepted ? (
              <input
                aria-label={`${label} in English`}
                placeholder="in English (optional)"
                value={settled.english}
                onChange={(event) => onChange({ ...settled, english: event.target.value })}
              />
            ) : (
              <TypedBox line={line} settled={settled} onChange={onChange} />
            )}
          </div>
        ) : (
          <div className="intake-missing">
            {asking ? (
              <span className="small muted">Looking for it on the page once more…</span>
            ) : offer ? (
              /* He chose to type over it. What the papers gave stays on
                 screen above his own value, one click from being used again. */
              <span className="small muted">
                The papers give{' '}
                <span className={hindi ? 'deva' : undefined}>{offer.as_written}</span> (
                {offer.filename}, page {offer.page}).{' '}
                <button
                  type="button"
                  className="link"
                  data-testid={`restore-${line.field}`}
                  onClick={() => onChange({ ...settled, declined: false, typed: '' })}
                >
                  Use it
                </button>
              </span>
            ) : (
              <span className="small muted">
                {line.reason ?? 'Not in the papers.'}
                {line.field === 'name' ? '' : ' It may be left for the case page.'}
              </span>
            )}
            <TypedBox line={line} settled={settled} onChange={onChange} />
          </div>
        )}
      </dd>
    </div>
  )
}

/** The small box for a value the papers did not give (32.7, ruled 29 Sep). */
function TypedBox({
  line,
  settled,
  onChange,
}: {
  line: IntakeParticularView
  settled: Settled
  onChange: (next: Settled) => void
}) {
  const ticket = line.field === 'ticket'
  return (
    <span className="intake-typed">
      <input
        aria-label={`${line.label}, as the papers write it`}
        className={ticket ? 'mono' : 'deva'}
        inputMode={ticket ? 'numeric' : undefined}
        placeholder={ticket ? 'the digits' : 'as the papers write it'}
        value={settled.typed}
        onChange={(event) =>
          onChange({ ...settled, typed: event.target.value, accepted: false })
        }
        data-testid={`type-${line.field}`}
      />
      {ticket ? null : (
        <input
          aria-label={`${line.label} in English`}
          placeholder="in English (optional)"
          value={settled.english}
          onChange={(event) => onChange({ ...settled, english: event.target.value })}
        />
      )}
    </span>
  )
}

/* ------------------------------------------------- a case already on paper -- */

type ActChoice = {
  chosen: boolean
  on: string
  answers: Record<string, string>
}

function defaultChoice(act: IntakeActView): ActChoice {
  return {
    // The server says what starts ticked: only an act whose paper gives its
    // date, and never his answers — they decide his response, and every
    // charge admitted skips the inquiry (compliance review, 2 Oct 2026).
    chosen: Boolean(act.ready && act.preselected && act.on),
    on: act.on ?? '',
    answers: Object.fromEntries(
      (act.answers ?? []).map((answer) => [String(answer.charge), answer.answer ?? '']),
    ),
  }
}

/**
 * The acts the papers show (docs/24 item 6, docs/22 Case 2 step 4): offered for
 * the officer to confirm, each with the date the paper gives, recorded at
 * Confirm through the one transition path in date order. **Never recorded on
 * the app's say-so** — acts start and stop the clocks. An act that cannot be
 * recorded yet stays on screen, greyed, with what it needs.
 */
function ActsCard({
  acts,
  choices,
  onChange,
}: {
  acts: IntakeActView[]
  choices: Record<string, ActChoice>
  onChange: (key: string, choice: ActChoice) => void
}) {
  return (
    <div className="card" data-testid="confirm-acts">
      <header>
        <h2>What has happened, as the papers show it</h2>
        <span className="small muted">
          ticked ones are recorded when you confirm, in date order
        </span>
      </header>
      <div className="body">
        {acts.map((act) => {
          const choice = choices[act.key] ?? defaultChoice(act)
          const disabled = !act.ready
          return (
            <div key={act.key} className={`intake-act${disabled ? ' blocked-act' : ''}`}>
              <label className="inline-choice">
                <input
                  type="checkbox"
                  checked={choice.chosen && !disabled}
                  disabled={disabled}
                  onChange={(event) =>
                    onChange(act.key, { ...choice, chosen: event.target.checked })
                  }
                  data-testid={`act-${act.key}`}
                />
                <span>
                  <strong>{act.label}</strong>
                  {act.detail ? <span className="small muted"> — {act.detail}</span> : null}
                  {sourceWords(act.source) ? (
                    <span className="small muted"> ({sourceWords(act.source)})</span>
                  ) : null}
                </span>
              </label>
              {disabled ? (
                <p className="small muted">{act.why_not}</p>
              ) : (
                <div className="row form">
                  <div className="field">
                    <label htmlFor={`act-on-${act.key}`}>On</label>
                    <input
                      id={`act-on-${act.key}`}
                      type="date"
                      value={choice.on}
                      onChange={(event) =>
                        onChange(act.key, { ...choice, on: event.target.value })
                      }
                    />
                  </div>
                </div>
              )}
              {act.key === 'answers' && !disabled ? (
                <table className="intake-answers">
                  <tbody>
                    {(act.answers ?? []).map((answer) => (
                      <tr key={answer.charge}>
                        <td className="mono">{answer.charge}</td>
                        <td>
                          {answer.heading}
                          {answer.quote ? (
                            <details>
                              <summary className="small muted">his words</summary>
                              <p className="small deva">{answer.quote}</p>
                            </details>
                          ) : null}
                        </td>
                        <td>
                          <select
                            aria-label={`His answer to charge ${answer.charge}`}
                            value={choice.answers[String(answer.charge)] ?? ''}
                            onChange={(event) =>
                              onChange(act.key, {
                                ...choice,
                                answers: {
                                  ...choice.answers,
                                  [String(answer.charge)]: event.target.value,
                                },
                              })
                            }
                          >
                            <option value="">— his answer —</option>
                            <option value="admitted">Admitted</option>
                            <option value="not_admitted">Not admitted</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Step 2 of the one door, for a case already on paper: add what the file has.
 *
 * **Each paper's kind is picked from the paper kinds** (docs/24 item 6) — the
 * office's own names for what comes onto a file, never a register document or
 * a D-code. The kind is the officer's word, so the paper is read once, with
 * that kind's questions; a kind that *is* an external document on the rail
 * (the enquiry report, the inquiry report, the penalty order) also records
 * it, through the label's own path, exactly as the case page does.
 *
 * Every paper is optional; the officer says when the papers are in.
 */
function PapersStep({ caseId, onDone }: { caseId: string; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)
  const [failure, setFailure] = useState<unknown>(null)
  const detail = useQuery({ queryKey: ['case', caseId], queryFn: () => api.getCase(caseId) })
  const papers = useQuery({
    queryKey: ['source-files', caseId],
    queryFn: () => api.listSourceFiles(caseId),
    refetchIntervalInBackground: true,
    refetchInterval: (query) => papersPollInterval(query.state.data?.files ?? []),
  })

  const rows = (papers.data?.files ?? []).filter((row) => !row.removed_at)
  const removedRows = (papers.data?.files ?? []).filter((row) => row.removed_at)
  const kinds = (detail.data?.paper_kinds ?? []).filter((kind) => kind.key !== 'other')
  const selectedRow = rows.find((row) => row.id === selected) ?? null

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['source-files', caseId] })
    void queryClient.invalidateQueries({ queryKey: ['case', caseId] })
    void queryClient.invalidateQueries({ queryKey: ['intake', caseId] })
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: selectedRow ? 'minmax(0, 1fr) minmax(320px, 40%)' : '1fr',
        gap: 16,
        alignItems: 'start',
      }}
    >
      <div>
        <PaperAdd
          caseId={caseId}
          kinds={kinds}
          atCourt={isCourtDocument(detail.data?.declared_reached)}
          onDone={refresh}
          onError={setFailure}
        />

        {rows.length === 0 ? (
          <p className="small muted">No papers yet.</p>
        ) : (
          <div className="papers-list">
            <div className="paper-line paper-head small muted" aria-hidden="true">
              <span>Paper</span>
              <span>File</span>
              <span>What it is</span>
              <span>Added</span>
              <span />
            </div>
            {rows.map((row) => (
              <PaperRow
                key={row.id}
                caseId={caseId}
                row={row}
                kinds={kinds}
                selected={row.id === selected}
                onSelect={() => setSelected(row.id === selected ? null : row.id)}
                onDone={refresh}
                onError={setFailure}
              />
            ))}
          </div>
        )}

        {removedRows.length > 0 ? (
          <div className="card" data-testid="backfill-removed">
            <header>
              <span>Removed from the file</span>
              <span className="small muted">each keeps its reason</span>
            </header>
            <div className="body">
              {removedRows.map((row) => (
                <p key={row.id} className="small muted">
                  paper {row.ordinal} · <span className="mono">{row.filename}</span> — removed
                  by {row.removed_by}: {row.removal_reason}
                </p>
              ))}
            </div>
          </div>
        ) : null}

        <ErrorEnvelope error={failure} />

        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
          <button
            type="button"
            className="btn primary"
            data-testid="papers-done"
            disabled={rows.length === 0}
            onClick={onDone}
          >
            That is the file — read it and show what it says
          </button>
          {rows.length === 0 ? (
            <span className="small muted">Add at least one paper first.</span>
          ) : null}
        </div>
      </div>

      {selectedRow ? <PaperPreview caseId={caseId} row={selectedRow} /> : null}
    </div>
  )
}

function PaperAdd({
  caseId,
  kinds,
  atCourt = false,
  onDone,
  onError,
}: {
  caseId: string
  kinds: PaperKindOption[]
  /** The officer said the file has reached the Tribunal or the High Court: the
   *  papers of the court case are offered first. */
  atCourt?: boolean
  onDone: () => void
  onError: (failure: unknown) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [kind, setKind] = useState('')
  // Which pleading a Body or a file of Annexures belongs to. Left unsaid, the
  // app tells from the paper's first page and says how it took it; said here,
  // it is the officer's word.
  const [pleading, setPleading] = useState<PleadingKey | ''>('')
  const asksPleading = belongsToPleading(kind)
  const upload = useMutation({
    mutationFn: (file: File) => {
      const option = kinds.find((candidate) => candidate.key === kind)
      return api.uploadSourceFile(caseId, file, {
        paper_kind: kind,
        // A kind that is an external document on the rail records it too, the
        // way the case page's own upload does (SomethingCameIn).
        ...(option?.records_document ? { document_type: option.records_document } : {}),
        ...(asksPleading && pleading
          ? { other_description: uploadDescription(kind as CourtKind, pleading) }
          : {}),
      })
    },
    onSuccess: () => {
      setKind('')
      setPleading('')
      if (fileInput.current) fileInput.current.value = ''
      onDone()
    },
    onError,
  })
  const courtKeys = new Set<string>(COURT_KINDS.map((entry) => entry.key))
  const courtKinds = kinds.filter((option) => courtKeys.has(option.key))
  const otherKinds = kinds.filter((option) => !courtKeys.has(option.key))

  return (
    <div className="card">
      <header>
        <span>Add a paper</span>
        <span className="small muted">one file is one paper — say what it is</span>
      </header>
      <div className="body">
        {atCourt ? <CourtUploadGuide /> : null}
        <div className="row form">
          <div className="field">
            <label htmlFor="backfill-file">The paper</label>
            <input
              id="backfill-file"
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
              disabled={upload.isPending}
            />
          </div>
          <div className="field">
            <label htmlFor="backfill-kind">What it is</label>
            <select
              id="backfill-kind"
              aria-label="What it is"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              data-testid="paper-kind"
            >
              <option value="">— choose —</option>
              {atCourt && courtKinds.length > 0 ? (
                /* A file that has reached the Tribunal or the High Court: its
                   court papers first, under their own heading, and everything
                   else below — nothing is taken off the list. */
                <>
                  <optgroup label="Papers of the court case">
                    {courtKinds.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Other papers of the file">
                    {otherKinds.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                    <option value="other">Something else</option>
                  </optgroup>
                </>
              ) : (
                <>
                  {kinds.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                  <option value="other">Something else</option>
                </>
              )}
            </select>
          </div>
          {asksPleading ? (
            <div className="field">
              <label htmlFor="backfill-pleading">Which pleading</label>
              <select
                id="backfill-pleading"
                aria-label="Which pleading"
                value={pleading}
                onChange={(event) => setPleading(event.target.value as PleadingKey | '')}
                data-testid="paper-pleading"
              >
                <option value="">— tell from its first page —</option>
                {PLEADINGS.filter(
                  (option) => kind === 'pleading-body' || !('bodyOnly' in option),
                ).map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <button
            className="btn primary"
            data-testid="backfill-add"
            disabled={upload.isPending || !kind}
            onClick={() => {
              const file = fileInput.current?.files?.[0]
              if (file) upload.mutate(file)
            }}
          >
            {upload.isPending ? 'Adding…' : 'Add the paper'}
          </button>
        </div>
        {!kind ? (
          <p className="small muted">Choose what the paper is — it is read once, as that.</p>
        ) : null}
      </div>
    </div>
  )
}

function PaperRow({
  caseId,
  row,
  kinds,
  selected,
  onSelect,
  onDone,
  onError,
}: {
  caseId: string
  row: SourceFileView
  kinds: PaperKindOption[]
  selected: boolean
  onSelect: () => void
  onDone: () => void
  onError: (failure: unknown) => void
}) {
  const [removing, setRemoving] = useState(false)
  const [removeReason, setRemoveReason] = useState('')
  const remove = useMutation({
    mutationFn: () =>
      api.removeSourceFile(caseId, row.id, { preflight: false, reason: removeReason.trim() }),
    onSuccess: onDone,
    onError,
  })
  const kindLabel =
    kinds.find((option) => option.key === row.paper_kind)?.label ??
    (row.paper_kind === 'other' ? 'Something else' : null)
  // Which pleading a Body or a file of Annexures was marked as — shown, and
  // changed here. It was shown nowhere on this screen, so a file marked as the
  // wrong pleading was found out only on the reply's page, and put right only
  // by taking the file off and adding it again.
  const asksPleading = belongsToPleading(row.paper_kind ?? '')
  const pleading = pleadingOfDescription(row.other_description)
  const relabel = useMutation({
    mutationFn: (next: PleadingKey) =>
      api.labelSourceFile(caseId, row.id, {
        preflight: false,
        other_description: uploadDescription(row.paper_kind as CourtKind, next),
      }),
    onSuccess: onDone,
    onError,
  })

  return (
    <div
      className={`paper${selected ? ' up-next' : ''}`}
      data-testid={`backfill-paper-${row.ordinal}`}
    >
      <div className="paper-line small">
        <span className="muted mono">{row.ordinal}</span>
        <button type="button" className="link mono" onClick={onSelect}>
          {row.filename}
        </button>
        <span className={kindLabel ? undefined : 'muted'}>
          {kindLabel ?? 'not yet named'}
          {asksPleading ? (
            <>
              {' · '}
              <select
                aria-label={`Which pleading ${row.filename} is`}
                value={pleading ?? ''}
                disabled={relabel.isPending}
                onChange={(event) => {
                  if (event.target.value) relabel.mutate(event.target.value as PleadingKey)
                }}
                data-testid={`backfill-pleading-${row.ordinal}`}
              >
                {pleading ? null : <option value="">told from its first page</option>}
                {PLEADINGS.filter(
                  (option) => row.paper_kind === 'pleading-body' || !('bodyOnly' in option),
                ).map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </>
          ) : null}
        </span>
        <span className="mono muted">{readDate(readDay(row.created_at))}</span>
        <button
          type="button"
          className="btn ghost"
          data-testid={`backfill-remove-${row.ordinal}`}
          onClick={() => setRemoving(true)}
        >
          Remove
        </button>
      </div>
      {row.detected_multi_document === true && !row.officer_disposition ? (
        <p className="small warn-text">
          This file looks like more than one document — {row.detected_reason} Answer it on the
          case page, or remove and re-upload it split.
        </p>
      ) : null}
      {removing ? (
        <div className="row form">
          <div className="field">
            <label htmlFor={`backfill-remove-why-${row.id}`}>Why it comes off the file</label>
            <input
              id={`backfill-remove-why-${row.id}`}
              value={removeReason}
              autoFocus
              onChange={(event) => setRemoveReason(event.target.value)}
              placeholder="named wrongly — adding it again"
            />
          </div>
          <button
            type="button"
            className="btn"
            disabled={!removeReason.trim() || remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? 'Removing…' : 'Remove it'}
          </button>
          <button type="button" className="btn" onClick={() => setRemoving(false)}>
            Keep it
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The preview panel: a scanned PDF renders as itself; a Word file — which no
 * browser renders — shows the read text. An unread file says so in words.
 */
function PaperPreview({ caseId, row }: { caseId: string; row: SourceFileView }) {
  const isPdf = row.filename.toLowerCase().endsWith('.pdf')
  const text = useQuery({
    queryKey: ['source-file-text', caseId, row.id],
    queryFn: () => api.getSourceFileText(caseId, row.id),
    enabled: !isPdf,
  })
  return (
    <div className="card" data-testid="backfill-preview">
      <header>
        <span className="mono small">{row.filename}</span>
      </header>
      <div className="body">
        {isPdf ? (
          <iframe
            title={`Preview of ${row.filename}`}
            src={api.sourceFilePath(caseId, row.id)}
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        ) : text.isLoading ? (
          <p className="small muted">Reading…</p>
        ) : text.data?.text ? (
          <div
            className="deva"
            style={{ whiteSpace: 'pre-wrap', maxHeight: '65vh', overflowY: 'auto' }}
          >
            {text.data.text}
          </div>
        ) : (
          <p className="small muted">Nothing has been read from this file.</p>
        )}
      </div>
    </div>
  )
}
